import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { FileText, Send, Clock, CheckCircle, XCircle, Download, Plus, Upload, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Complete', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejete', class: 'bg-red-100 text-red-700', icon: XCircle },
};

export const RHDocuAC = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [contratSigne, setContratSigne] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API}/docu-client`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Filter only rh_contrat requests
        setRequests(data.filter(r => r.request_type === 'rh_contrat'));
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!contratSigne) {
      toast.error('Le contrat signe est obligatoire');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('request_type', 'rh_contrat');
      formData.append('contrat_signe', contratSigne);

      const response = await fetch(`${API}/docu-client`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Contrat envoye pour contre-signature');
        setIsFormOpen(false);
        setContratSigne(null);
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = async (requestId, fileType, filename) => {
    setDownloading(`${requestId}-${fileType}`);
    try {
      const response = await fetch(`${API}/docu-client/${requestId}/download/${fileType}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `${fileType}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Fichier telecharge');
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur de telechargement');
    } finally {
      setDownloading(null);
    }
  };

  const openDetail = (req) => {
    setSelectedRequest(req);
    setIsDetailOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="docu-ac-title">
            Docu AC
          </h1>
          <p className="text-slate-500 mt-1">Envoyez les contrats signes par vos agents commerciaux</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)} data-testid="new-contrat-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau contrat
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-50 to-teal-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-800">Comment ca marche ?</h3>
              <p className="text-sm text-emerald-700 mt-1">
                1. Televersez le contrat signe par l'agent commercial<br />
                2. L'administration contre-signe le contrat<br />
                3. Telechargez le contrat contre-signe
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests History */}
      <Card className="border-0 shadow-sm" data-testid="requests-table">
        <CardHeader>
          <CardTitle>Mes demandes de contrats</CardTitle>
          <CardDescription>Historique des contrats envoyes pour contre-signature</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Fichier envoye</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                    Aucune demande de contrat
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => {
                  const StatusIcon = statusConfig[req.status]?.icon || Clock;
                  return (
                    <TableRow key={req.request_id} className="hover:bg-slate-50">
                      <TableCell className="text-slate-500">
                        {new Date(req.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="font-medium">
                        {req.contrat_signe_filename || 'Contrat'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[req.status]?.class} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[req.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openDetail(req)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {req.status === 'completed' && req.response_filename && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => downloadFile(req.request_id, 'response', req.response_filename)}
                              disabled={downloading === `${req.request_id}-response`}
                            >
                              {downloading === `${req.request_id}-response` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Contrat contre-signe
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envoyer un contrat</DialogTitle>
            <DialogDescription>
              Televersez le contrat signe par l'agent commercial pour contre-signature
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contrat signe par l'AC *</Label>
              <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-emerald-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setContratSigne(e.target.files[0])}
                  className="hidden"
                  id="contrat-upload"
                />
                <label htmlFor="contrat-upload" className="cursor-pointer">
                  {contratSigne ? (
                    <>
                      <FileText className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                      <p className="font-medium text-emerald-600">{contratSigne.name}</p>
                      <p className="text-xs text-slate-500 mt-1">Cliquez pour changer</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                      <p className="text-slate-600">Cliquez pour telecharger</p>
                      <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !contratSigne}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Details de la demande</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Date d'envoi</p>
                  <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <p className="text-slate-500">Statut</p>
                  <Badge className={`${statusConfig[selectedRequest.status]?.class} mt-1`}>
                    {statusConfig[selectedRequest.status]?.label}
                  </Badge>
                </div>
              </div>

              {selectedRequest.response_comment && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-800">Commentaire admin:</p>
                  <p className="text-sm text-emerald-700">{selectedRequest.response_comment}</p>
                </div>
              )}

              {selectedRequest.rejection_reason && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">Motif du rejet:</p>
                  <p className="text-sm text-red-700">{selectedRequest.rejection_reason}</p>
                </div>
              )}

              <div className="space-y-2">
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => downloadFile(selectedRequest.request_id, 'contrat_signe', selectedRequest.contrat_signe_filename)}
                  disabled={downloading === `${selectedRequest.request_id}-contrat_signe`}
                >
                  {downloading === `${selectedRequest.request_id}-contrat_signe` ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Telecharger contrat envoye
                </Button>

                {selectedRequest.status === 'completed' && selectedRequest.response_filename && (
                  <Button 
                    className="w-full"
                    onClick={() => downloadFile(selectedRequest.request_id, 'response', selectedRequest.response_filename)}
                    disabled={downloading === `${selectedRequest.request_id}-response`}
                  >
                    {downloading === `${selectedRequest.request_id}-response` ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Telecharger contrat contre-signe
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
